import { useState } from 'react';

import { ButtonBase, Popover } from '@mui/material';
import {
  GridColumnVisibilityModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

import { Box } from '../box/box';
import { CHECKBOX_CHECK_PATH } from '../checkbox/check-path';
import { useWindowedTooltipZIndex } from '../dialog/windowed-tooltip-zindex-context';
import { useTheme } from '../styles/styles';

type ApiRef = ReturnType<typeof useGridApiRef>;

// Internal grid columns (checkbox, detail toggle, row actions) aren't listed.
const isListed = (field: string, headerName?: string) =>
  !field.startsWith('__') && !(field === 'actions' && !headerName);

const CheckMark = ({
  locked,
  checked,
}: {
  locked: boolean;
  checked: boolean;
}) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 18,
        height: 18,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '5px',
        background: locked
          ? theme.custom?.checkboxLockedBg
          : checked
          ? theme.custom?.listRowSelectedBg
          : theme.background.white,
        boxShadow: `inset 0 0 0 1.5px ${
          locked
            ? theme.custom?.checkboxLockedRing
            : checked
            ? theme.custom?.checkboxCheckedRing
            : theme.custom?.filterCheckboxRing
        }`,
        color: locked
          ? theme.custom?.columnsLockedHint
          : theme.colors.primary.main,
      }}
    >
      {checked && (
        <Box
          component="svg"
          viewBox="0 0 24 24"
          sx={{ width: 15, height: 15, fill: 'currentColor' }}
        >
          <path d={CHECKBOX_CHECK_PATH} />
        </Box>
      )}
    </Box>
  );
};

export const ColumnsPopover = ({
  apiRef,
  anchorEl,
  onClose,
}: {
  apiRef: ApiRef;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const open = Boolean(anchorEl);
  // Above a windowed (non-modal, raised) dialog.
  const layer = useWindowedTooltipZIndex();
  const [model, setModel] = useState<GridColumnVisibilityModel>({});

  const columns = open
    ? (apiRef.current?.getAllColumns?.() ?? []).filter((c) =>
        isListed(c.field, c.headerName)
      )
    : [];
  const pinnedModel = open ? apiRef.current?.getPinnedColumns?.() : undefined;
  const pinned = new Set([
    ...(pinnedModel?.left ?? []),
    ...(pinnedModel?.right ?? []),
  ]);
  const isVisible = (field: string) => model[field] !== false;
  const shown = columns.filter((c) => isVisible(c.field)).length;

  const update = (next: GridColumnVisibilityModel) => {
    setModel(next);
    apiRef.current?.setColumnVisibilityModel(next);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      TransitionProps={{
        onEnter: () =>
          setModel({
            ...(apiRef.current?.state?.columns?.columnVisibilityModel ?? {}),
          }),
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: -6, horizontal: 'right' }}
      sx={layer && { zIndex: layer.zIndex }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': 'Columns',
          sx: {
            width: 260,
            padding: '12px 8px 8px',
            boxSizing: 'border-box',
            borderRadius: '12px',
            boxShadow: theme.custom?.columnsPopoverShadow,
          },
        } as object,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          padding: '0 8px 8px',
        }}
      >
        <Box
          component="b"
          sx={{
            fontSize: '13.5px',
            fontWeight: 600,
            color: theme.custom?.brandNavy,
          }}
        >
          Columns
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: '12px',
            color: theme.custom?.tableHeaderText,
          }}
        >
          {shown} of {columns.length} shown
        </Box>
      </Box>
      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          display: 'grid',
          gap: '1px',
        }}
      >
        {columns.map((column) => {
          // Same rule as the grid's own dialog: pinned columns stay shown.
          const locked = column.hideable === false || pinned.has(column.field);
          const checked = locked || isVisible(column.field);
          return (
            <ButtonBase
              key={column.field}
              role="checkbox"
              aria-checked={checked}
              disabled={locked}
              onClick={() =>
                update({ ...model, [column.field]: !isVisible(column.field) })
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '10px',
                minHeight: 34,
                padding: '0 8px',
                borderRadius: '7px',
                fontSize: '13.5px',
                textAlign: 'left',
                color: locked
                  ? theme.custom?.shellSideIcon
                  : theme.custom?.brandNavy,
                '&:hover': {
                  background: locked
                    ? 'transparent'
                    : theme.custom?.columnsRowHoverBg,
                },
                '&.Mui-disabled': { color: theme.custom?.shellSideIcon },
              }}
            >
              <CheckMark locked={locked} checked={checked} />
              <Box
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {column.headerName || column.field}
              </Box>
              {locked && (
                <Box
                  component="small"
                  sx={{
                    fontSize: '11.5px',
                    color: theme.custom?.columnsLockedHint,
                  }}
                >
                  Always shown
                </Box>
              )}
            </ButtonBase>
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '8px',
          marginTop: '6px',
          padding: '8px 4px 0',
          borderTop: `1px solid ${theme.custom?.windowedHeaderLine}`,
        }}
      >
        <ButtonBase
          disabled={shown === columns.length}
          onClick={() =>
            update(Object.fromEntries(columns.map((c) => [c.field, true])))
          }
          sx={{
            height: 32,
            padding: '0 14px',
            borderRadius: '8px',
            border: `1px solid ${theme.custom?.controlBorder}`,
            background: theme.background.white,
            fontSize: '13.5px',
            color: theme.custom?.brandIndigo,
            '&:hover': { background: theme.custom?.listButtonHoverBg },
            '&.Mui-disabled': { opacity: 0.45 },
          }}
        >
          Show all
        </ButtonBase>
        <ButtonBase
          onClick={onClose}
          sx={{
            height: 32,
            padding: '0 14px',
            borderRadius: '8px',
            border: `1px solid ${theme.colors.primary.main}`,
            background: theme.colors.primary.main,
            fontSize: '13.5px',
            color: theme.background.white,
            '&:hover': { background: theme.colors.primary.dark },
          }}
        >
          Done
        </ButtonBase>
      </Box>
    </Popover>
  );
};
